const LOCAL_MAP_KEY_PREFIX = 'omniapps-idea:mindmap:local:';
const LOCAL_MAP_INDEX_KEY = 'omniapps-idea:mindmap:local:index';
const MAP_SAVE_PREFERENCES_KEY = 'omniapps-idea:mindmap:save-preferences';

function parseJSON(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readLocalIndex() {
  const index = parseJSON(localStorage.getItem(LOCAL_MAP_INDEX_KEY), []);
  return Array.isArray(index) ? index : [];
}

function writeLocalIndex(index) {
  localStorage.setItem(LOCAL_MAP_INDEX_KEY, JSON.stringify(index));
}

function localMapKey(mindmapId) {
  return `${LOCAL_MAP_KEY_PREFIX}${mindmapId}`;
}

function createId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function upsertLocalIndexEntry(entry) {
  const index = readLocalIndex();
  const nextIndex = [
    entry,
    ...index.filter((item) => item.id !== entry.id),
  ];
  writeLocalIndex(nextIndex);
}

function removeLocalIndexEntry(mindmapId) {
  const index = readLocalIndex();
  writeLocalIndex(index.filter((item) => item.id !== mindmapId));
}

function buildRootNode(mindmapId) {
  return {
    id: `${mindmapId}-root`,
    type: 'mindmap',
    parentId: null,
    position: { x: 0, y: 0 },
    data: { label: 'Central Idea' },
    created_at: Date.now(),
    updated_at: Date.now(),
  };
}

class LocalMindmapService {
  listLocalMindmaps() {
    const index = readLocalIndex();
    return index
      .map((item) => ({
        ...item,
        storage_type: 'local',
        updated_at: item.updated_at || Date.now(),
      }))
      .sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
  }

  createLocalMindmap(title = 'Untitled Mindmap') {
    const id = `local-${createId()}`;
    const now = Date.now();
    const rootNode = buildRootNode(id);

    const snapshot = {
      version: '1.0',
      mindmapId: id,
      title,
      savedAt: new Date(now).toISOString(),
      nodes: [rootNode],
      edges: [],
    };

    this.saveLocalMindmapData(id, snapshot, { updateIndex: true });
    this.setSavePreference(id, 'local');

    return {
      id,
      title,
      updated_at: now,
      storage_type: 'local',
    };
  }

  saveLocalMindmapData(mindmapId, snapshot, options = {}) {
    const now = Date.now();
    const normalizedSnapshot = {
      ...snapshot,
      mindmapId,
      title: snapshot?.title || 'Untitled Mindmap',
      savedAt: new Date(now).toISOString(),
      nodes: Array.isArray(snapshot?.nodes) ? snapshot.nodes : [],
      edges: Array.isArray(snapshot?.edges) ? snapshot.edges : [],
    };

    localStorage.setItem(localMapKey(mindmapId), JSON.stringify(normalizedSnapshot));

    if (options.updateIndex) {
      upsertLocalIndexEntry({
        id: mindmapId,
        title: normalizedSnapshot.title,
        updated_at: now,
      });
    }

    return normalizedSnapshot;
  }

  loadLocalMindmapData(mindmapId) {
    return parseJSON(localStorage.getItem(localMapKey(mindmapId)), null);
  }

  deleteLocalMindmap(mindmapId) {
    localStorage.removeItem(localMapKey(mindmapId));
    removeLocalIndexEntry(mindmapId);

    const preferences = parseJSON(localStorage.getItem(MAP_SAVE_PREFERENCES_KEY), {});
    if (preferences && typeof preferences === 'object' && mindmapId in preferences) {
      delete preferences[mindmapId];
      localStorage.setItem(MAP_SAVE_PREFERENCES_KEY, JSON.stringify(preferences));
    }
  }

  setSavePreference(mindmapId, location) {
    const nextLocation = location === 'local' ? 'local' : 'cloud';
    const preferences = parseJSON(localStorage.getItem(MAP_SAVE_PREFERENCES_KEY), {});
    const nextPreferences = {
      ...(preferences && typeof preferences === 'object' ? preferences : {}),
      [mindmapId]: nextLocation,
    };
    localStorage.setItem(MAP_SAVE_PREFERENCES_KEY, JSON.stringify(nextPreferences));
  }

  getSavePreference(mindmapId, fallback = 'cloud') {
    const preferences = parseJSON(localStorage.getItem(MAP_SAVE_PREFERENCES_KEY), {});
    const selected = preferences && typeof preferences === 'object' ? preferences[mindmapId] : undefined;
    if (selected === 'local' || selected === 'cloud') {
      return selected;
    }
    return fallback;
  }
}

export const localMindmapService = new LocalMindmapService();
export { LOCAL_MAP_KEY_PREFIX };
